import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {BehaviorSubject, forkJoin, merge, Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {NameValueDTO} from '../../../../../../common/models/NameValueDTO';
import {LookupValue} from '../../../../../types/lookup-value.enum';
import {LookupService} from '../../../../../../common/services/lookup.service';
import {CaseHospitalDTO} from '../../../../../models/case-hospital/CaseHospitalDTO';
import {SharedService} from '../../../../../../common/services/shared-service';
import {CustomValidator} from '../../../../../../common';
import {ProcessingClassSubType} from '../../../../../types/types';

enum Controls {
  WorkmansCompensationCaseNumber = 'workmansCompensationCaseNumber',
  RoadAccidentFundCaseNumber = 'roadAccidentFundCaseNumber',
  TransportType = 'transportType',
  ReasonForAdmission = 'reasonForAdmission',
  CommunicationNumber = 'communicationNumber',
}

@Component({
  selector: 'app-case-details-step',
  templateUrl: './case-details-step.component.html',
  styleUrls: [
    '../../../../../../../../styles/global/_add-view-edit-styles.scss'
  ]
})
export class CaseDetailsStepComponent implements OnChanges, OnInit, OnDestroy {
  readonly controls = Controls;
  readonly lookupValue = LookupValue;
  readonly destroyed$ = new Subject();
  readonly lookupValuesFetched$ = new BehaviorSubject(null);
  readonly processingClassSubTypeEnum = ProcessingClassSubType;

  @Input() data: CaseHospitalDTO;
  @Input() formGroup: FormGroup;
  @Input() isEditMode: boolean;
  @Input() processingClassSubtype!: ProcessingClassSubType;

  @Output() updateForm: EventEmitter<FormGroup> = new EventEmitter();

  transportTypeLookUpValues: NameValueDTO[] = [];
  reasonForAdmissionLookUpValues: NameValueDTO[] = [];

  constructor(private lookupService: LookupService,
              private formBuilder: FormBuilder,
              private sharedService: SharedService,
            ) {
            }

  get communicationNumberAbstractControl() {
    return this.formGroup.get(Controls.CommunicationNumber);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isEditMode) {
      this.isEditMode = changes.isEditMode.currentValue;
    }
    if (changes.formGroup) {
      this.formGroup = changes.formGroup.currentValue;
    }
    if (changes.data && changes.data.currentValue) {

      if (this.processingClassSubtype === ProcessingClassSubType.Pharmacy) {
        this.setFormsForEdit(this.data);
      }
      else {
        this.lookupValuesFetched$
          .pipe(
            takeUntil(this.destroyed$)
          )
          .subscribe((isLookupValuesFetchedAndSet) => {
            if (this.isEditMode && isLookupValuesFetchedAndSet) {
              this.data = changes.data.currentValue;
              this.setFormsForEdit(this.data);
            }
          });
      }
    }
  }

  ngOnInit(): void {
    if (this.processingClassSubtype !== ProcessingClassSubType.Pharmacy) {
      this.initLookUpValues();
    }
    this.initFormGroup();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initFormGroup() {
    this.initFormGroupByMedicalProcessingClassSubType();
  }

  initFormGroupByMedicalProcessingClassSubType(): void {
    if (this.processingClassSubtype === ProcessingClassSubType.Hospital) {
      this.formGroup = this.formBuilder.group({
        [Controls.TransportType]: new FormControl(null, !this.isEditMode ? [Validators.required] : []),
        [Controls.ReasonForAdmission]: new FormControl(null, !this.isEditMode ? [Validators.required] : []),
        [Controls.WorkmansCompensationCaseNumber]: new FormControl(null),
        [Controls.RoadAccidentFundCaseNumber]: new FormControl(null),
        [Controls.CommunicationNumber]: new FormControl(null, [Validators.required, CustomValidator.mobileNumberValidator]),
      });

      this.formGroup.get(Controls.TransportType).setValue(1);
      this.formGroup.get(Controls.ReasonForAdmission).setValue(1);
    }
    else if (this.processingClassSubtype === ProcessingClassSubType.Pharmacy) {
      this.formGroup = this.formBuilder.group({
        [Controls.TransportType]: new FormControl(null),
        [Controls.ReasonForAdmission]: new FormControl(null),
        [Controls.WorkmansCompensationCaseNumber]: new FormControl(null),
        [Controls.RoadAccidentFundCaseNumber]: new FormControl(null),
        [Controls.CommunicationNumber]: new FormControl(null, [Validators.required, CustomValidator.mobileNumberValidator]),
      });

      this.formGroup.get(Controls.TransportType).setValue(1);
      this.formGroup.get(Controls.ReasonForAdmission).setValue(1);
    }

    // Notify Updated Reference of FormGroup to parent:
    this.updateForm.next(this.formGroup);

    merge(
      this.formGroup.get(Controls.TransportType).valueChanges,
      this.formGroup.get(Controls.ReasonForAdmission).valueChanges,
      this.formGroup.get(Controls.CommunicationNumber).valueChanges,
    ).pipe(
      takeUntil(this.destroyed$)
    )
      .subscribe(_ => {
        this.updateForm.next(this.formGroup);
      });
  }

  initLookUpValues() {
    forkJoin(
      this.lookupService.getListOfLookupValues(this.lookupValue.TransportType),
      this.lookupService.getListOfLookupValues(this.lookupValue.ReasonForAdmission)
    )
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          if (response[0]) {
            this.transportTypeLookUpValues = response[0];
          }
          if (response[1]) {
            this.reasonForAdmissionLookUpValues = response[1];
          }
          this.lookupValuesFetched$.next(true);
        },
        (error) => {
          console.log(error);
        }
      );
  }

  resetDetailsFormGroup() {
    this.formGroup.reset();
  }

  getPayload() {
    const transportValue = this.formGroup.controls[Controls.TransportType].value;
    const reasonForAdmissionValue = this.formGroup.controls[Controls.ReasonForAdmission].value;

    return {
      workmanCompensationCaseInd: null,
      roadAccidentFundCaseInd: null,
      [Controls.WorkmansCompensationCaseNumber]: this.formGroup.controls[Controls.WorkmansCompensationCaseNumber].value,
      [Controls.RoadAccidentFundCaseNumber]: this.formGroup.controls[Controls.RoadAccidentFundCaseNumber].value,
      [Controls.TransportType]: this.transportTypeLookUpValues.find((item: NameValueDTO) => item.value === transportValue),
      [Controls.ReasonForAdmission]: this.reasonForAdmissionLookUpValues.find((item: NameValueDTO) => item.value === reasonForAdmissionValue),
      [Controls.CommunicationNumber]: this.formGroup.controls[Controls.CommunicationNumber].value,
    };
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  private setFormsForEdit(data: CaseHospitalDTO) {
    const {
      transportType,
      reasonForAdmission,
      workmansCompensationCaseNumber,
      roadAccidentFundCaseNumber,
      caseDTO
    } = data;

    this.formGroup.patchValue({
      [Controls.TransportType]: this.transportTypeLookUpValues.find((item: NameValueDTO): boolean => item.value === transportType?.value)?.value,
      [Controls.ReasonForAdmission]: this.reasonForAdmissionLookUpValues.find((item: NameValueDTO): boolean => item.value === reasonForAdmission?.value)?.value,
      [Controls.WorkmansCompensationCaseNumber]: workmansCompensationCaseNumber,
      [Controls.RoadAccidentFundCaseNumber]: roadAccidentFundCaseNumber,
      [Controls.CommunicationNumber]: caseDTO.communicationNumber
    });
  }
}
